import { useState, useCallback, useEffect, useMemo } from 'react';
import { parse } from 'date-fns';
import { useCalendarSettings } from '../../../lib';
import { formatDateForInput } from '.';

export const useToolbarLogic = (date: Date, view: string, onNavigate: Function, onView: Function) => {
	const { settings } = useCalendarSettings();
	const [inputValue, setInputValue] = useState<string>(() => formatDateForInput(date, view));

	useEffect(() => {
		setInputValue(formatDateForInput(date, view));
	}, [date, view]);

	const inputType = view === 'month' || view === 'agenda' ? 'month' : 'date';

	const handlePrevClick = useCallback(() => onNavigate('PREV'), [onNavigate]);
	const handleNextClick = useCallback(() => onNavigate('NEXT'), [onNavigate]);
	const handleTodayClick = useCallback(() => onNavigate('TODAY'), [onNavigate]);

	const handleDateInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			let newDate: Date | null = null;

			try {
				if (inputType === 'month') {
					newDate = parse(value, 'yyyy-MM', new Date());
				} else if (inputType === 'date') {
					newDate = parse(value, 'yyyy-MM-dd', new Date());
				}
			} catch (error) {
				console.error('Invalid date format', error);
			}

			if (newDate) {
				onNavigate('DATE', newDate);
			}
		},
		[onNavigate, inputType]
	);

	const createViewChangeHandler = useCallback(
		(viewType: string) => () => {
			onView(viewType);
		},
		[onView]
	);

	const viewLabels: { [key: string]: string } = useMemo(
		() => ({
			month: settings.monthButtonLabel || 'Month',
			week: settings.weekButtonLabel || 'Week',
			day: settings.dayButtonLabel || 'Day',
			agenda: settings.agendaButtonLabel || 'Agenda',
		}),
		[settings]
	);

	return {
		inputValue,
		inputType,
		handlePrevClick,
		handleNextClick,
		handleTodayClick,
		handleDateInputChange,
		createViewChangeHandler,
		viewLabels,
	};
};
